import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Components, Feedback, Utils, translate,
} from 'versafleet-core';
import * as Sentry from '@sentry/browser';
import JobModule from 'modules/job';
import ReduxActions from 'reduxActions';
import { isUserReadOnly } from 'utils/accessHelper';

const {
  FullScreenLoading, Formatter, Helper, TranslationHelper, PusherHelper,
} = Utils;

const SORT_OPTIONS = [{
  value: 'id',
  text: 'Created at',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'updated_at',
  text: 'Last updated',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'jobs.time_from',
  text: 'Job Time window',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}];

class SubmissionsJobPageContent extends React.Component {
  constructor(props) {
    super(props);
    this.dataId = null;
    this.state = {
      checkedStatus: {},
      filter: this._getDefaultFilter(),
      disableButton: { print: false },
      showCancelModal: false,
      showFilter: false,
    };
  }

  UNSAFE_componentWillMount() {
    this.props.dispatch(ReduxActions.constant.fetchList());
    this.props.dispatch(ReduxActions.account.fetch());
    this.props.dispatch(ReduxActions.account.fetchTimeWindows());
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.meta !== this.props.meta) {
      this._updateCheckedStatus({});
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.filter !== this.state.filter) {
      this._loadList();
    }
  }

  _toggleShowFilter = () => {
    this.setState(prevState => ({ showFilter: !prevState.showFilter }));
  }

  _resetFilters() {
    this.setState({ filter: this._getDefaultFilter() }, () => {
      this._loadList();
    });
  }

  _getDefaultFilter() {
    const { date, job_type: jobType } = this.props.filter;

    return {
      date: Array.isArray(date) ? date : [date, date],
      keyword: [],
      state: this._getJobStatusOptions().map(option => option.value),
      sort_by: SORT_OPTIONS[0].value,
      order_by: 'desc',
      page: 1,
      per_page: 20,
      time_window_id: [],
      time_from: null,
      time_to: null,
      tags: [],
      job_type: Helper.isNullOrUndefined(jobType)
        ? this._getJobTypeOptions().map(option => option.value) : jobType,
    };
  }

  _getStatusFilterText() {
    const { strings } = this.props;
    const { filter } = this.state;
    const taskStatusOptions = this._getJobStatusOptions();
    const taskStatusCount = filter.state.length > 0
      && filter.state.length <= taskStatusOptions.length ? filter.state.length : null;

    let text;
    if (taskStatusCount === 1) {
      text = Formatter.capitalizeFirstLetter(filter.state[0]);
    } else if (taskStatusCount === taskStatusOptions.length) {
      text = `${strings.Filter.buttons.all} ${strings.Filter.buttons.status.itemPlural}`;
    } else if (taskStatusCount === 0 || taskStatusCount === null) {
      text = `${strings.Filter.buttons.no} ${strings.Filter.buttons.status.item}`;
    } else {
      text = `${taskStatusCount} ${strings.Filter.buttons.status.itemPlural}...`;
    }

    return text;
  }

  _getTypeFilterText() {
    const { strings } = this.props;
    const { filter } = this.state;
    const jobTypeOptions = this._getJobTypeOptions();
    const taskTypeCount = filter.job_type.length > 0
      && filter.job_type.length <= jobTypeOptions.length ? filter.job_type.length : null;

    let text;
    if (taskTypeCount === 1) {
      text = strings.Filter.buttons.taskType.type[filter.job_type[0]];
    } else if (taskTypeCount === jobTypeOptions.length) {
      text = `${strings.Filter.buttons.all} ${strings.Filter.buttons.jobType.itemPlural}`;
    } else if (taskTypeCount === 0 || taskTypeCount === null) {
      text = `${strings.Filter.buttons.no} ${strings.Filter.buttons.jobType.item}`;
    } else {
      text = `${taskTypeCount} ${strings.Filter.buttons.jobType.itemPlural}...`;
    }

    return text;
  }

  _getTagFilterText() {
    const { jobTags, strings } = this.props;
    const value = this.state.filter.tags;
    const options = jobTags.map(option => ({ value: option, text: option }));

    const text = value.length <= 1 ? strings.Filter.buttons.tag.job.item : `${value.length > 1
      && value.length < options.length ? strings.Filter.buttons.tag.multiple
      : strings.Filter.buttons.all} ${strings.Filter.buttons.tag.job.itemPlural}`;

    return text;
  }

  _getSubTextJobTimeRange() {
    const { time_from: timeFrom, time_to: timeTo } = this.state.filter;

    return `${Helper.getTimeFromSecond(timeFrom * 60)} - ${Helper.getTimeFromSecond(timeTo * 60)}`;
  }

  _getModelOptions() {
    const strings = this.props.strings.Job.constants;
    return [
      { value: 'task', text: Formatter.capitalizeFirstLetter(strings.taskPlural) },
      { value: 'job', text: Formatter.capitalizeFirstLetter(strings.jobPlural) },
    ];
  }

  _getJobStatusOptions() {
    const strings = this.props.strings.Job.status;
    return [{
      text: strings.draft,
      value: 'draft',
    }, {
      text: strings.rejected,
      value: 'rejected',
    }, {
      text: strings.submitted,
      value: 'submitted',
    }, {
      text: strings.cancelled,
      value: 'cancelled',
    }];
  }

  _getJobTypeOptions() {
    const strings = this.props.strings.Job.constants;
    return [{
      text: Formatter.capitalizeFirstLetter(strings.pickup),
      value: 'pickup',
    }, {
      text: Formatter.capitalizeFirstLetter(strings.delivery),
      value: 'delivery',
    }, {
      text: strings.pointToPoint,
      value: 'p2p',
    }];
  }

  _getSelectedIds() {
    return Object.keys(this.state.checkedStatus)
      .filter(key => this.state.checkedStatus[key])
      .map(key => parseInt(key, 10));
  }

  _onFilterChange = (fieldName, value) => {
    const { filter: filterState } = this.state;
    const filter = Helper.setFilter(filterState, fieldName, value);
    const myJobsFilter = this.props.filter;

    // Each time a filter is changed, check if any of the keys exist in
    // the filter store. If so, update the filter store to the filter changed
    Object.keys(myJobsFilter).forEach((key) => {
      if (typeof filter[key] !== 'undefined') {
        myJobsFilter[key] = filter[key];
      }
    });
    this.props.dispatch(ReduxActions.filter.updateMyJobsFilter(myJobsFilter));

    this.setState({ filter });
  }

  _onModelChange = (value) => {
    this.props.onModelChange(value);
  }

  _loadList() {
    FullScreenLoading.FullScreenLoadingManager.open(this.props.strings.Job.page.loadingItems);
    this.props.dispatch(ReduxActions.job.fetchList(this.state.filter))
      .then(() => {
        FullScreenLoading.FullScreenLoadingManager.close();
      });
  }

  _checkItem = (id) => {
    this.setState(prevState => ({
      checkedStatus: {
        ...prevState.checkedStatus,
        [id]: !prevState.checkedStatus[id],
      },
    }));
  }

  _updateCheckedStatus = (checkedStatus) => {
    this.setState({ checkedStatus });
  }

  _getJobWithId(id) {
    for (let i = 0; i < this.props.jobs.length; i += 1) {
      if (this.props.jobs[i].id === id) {
        return this.props.jobs[i];
      }
    }
    return null;
  }

  _isAssigned(id) {
    return (this._getJobWithId(id) || {}).state === 'draft';
  }

  _isSubmitted(id) {
    return (this._getJobWithId(id) || {}).state === 'submitted';
  }

  _viewNewJobForm = () => {
    Utils.Router.transitionTo('/submissions/jobs/new');
  }

  _viewImportJobForm = () => {
    Utils.Router.transitionTo('/submissions/jobs/import');
  }

  _viewExportJobForm = () => {
    Utils.Router.transitionTo('/submissions/jobs/export');
  }

  _submitMultiple = () => {
    const ids = this.props.jobs
      .filter(job => this.state.checkedStatus[job.id] && (job.state === 'draft' || job.state === 'rejected'))
      .map(job => job.id);

    if (ids.length > 0) {
      this.props.dispatch(ReduxActions.job.submitMultiple(ids));
    } else {
      this._loadList();
    }
  }

  _revokeMultiple = () => {
    const ids = this.props.jobs
      .filter(job => this.state.checkedStatus[job.id] && job.state === 'submitted')
      .map(job => job.id);

    if (ids.length > 0) {
      this.props.dispatch(ReduxActions.job.revokeMultiple(ids));
    } else {
      this._loadList();
    }
  }

  _archiveMultiple = () => {
    const ids = this._getSelectedIds();
    if (ids.length > 0) {
      this.props.dispatch(ReduxActions.job.archiveMultiple(ids));
    } else {
      this._loadList();
    }
  }

  _printMultiple = () => {
    const pusherId = `job_print_${PusherHelper.getNewPusherId()}`;
    const query = { ids: this._getSelectedIds(), pusher_id: pusherId };
    const printJobs = () => {
      PusherHelper.bindChannel('user', pusherId,
        result => this._handlePrintResult(result, pusherId));
      ReduxActions.job.printMultiple(query);
    };
    Feedback.Progress.Manager.updateProgress(pusherId, { onRetry: printJobs });
    printJobs();
    this._disableButton('print');
  }

  _handlePrintResult = (result, pusherId) => {
    Feedback.Progress.Manager.updateProgress(pusherId, result);
    if (result.status === 'failed') {
      PusherHelper.unbindChannel('user', pusherId);
      Sentry.captureException(result.error);
    }
    if (result.status === 'completed') {
      window.open(result.url, '_blank');
      PusherHelper.unbindChannel('user', pusherId);
    }
  }

  _disableButton(buttonName) {
    this.setState(prevState => ({
      disableButton: { ...prevState.disableButton, [buttonName]: true },
    }), async () => {
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.setState(prevState => ({
        disableButton: { ...prevState.disableButton, [buttonName]: false },
      }));
    });
  }

  _showCancelModal(dataId) {
    this.setState({ showCancelModal: true });
    this.dataId = dataId;
  }

  _hideCancelModal() {
    this.setState({ showCancelModal: false });
    this.dataId = null;
  }

  async _confirmCancel() {
    const strings = this.props.strings.Job;

    try {
      Utils.FullScreenLoading.FullScreenLoadingManager.open(strings.page.cancellingItem);
      await this.props.dispatch(ReduxActions.job.cancel(this.dataId));
      Utils.SuccessMessage.SuccessMessageManager.flash(
        <JobModule.CancelSuccessMessage count={1} />,
      );
    } catch (error) {
      Utils.SuccessMessage.SuccessMessageManager.flash(
        <div className="vf-job__cancel-dialog__error-message">
          {Utils.TranslationHelper.replaceTag(strings.cancelDialog.errorMessage, {
            count: 1,
          })}
        </div>,
      );
    }

    this.setState({ showCancelModal: false });
    Utils.FullScreenLoading.FullScreenLoadingManager.close();
  }

  _renderFilterPanel = () => (
    <Components.ActionPanel.FilterPanel
      rightAnchoredButtons={[
        <Components.ActionPanel.FilterResetButton
          onClick={() => this._resetFilters()}
        />,
      ]}
      showFilter={this.state.showFilter}
    >
      <Components.ActionPanel.DropdownButton
        showArrow
        text={this._getStatusFilterText()}
      >
        <Components.Input.CheckboxGroup.WithArray
          onChange={value => this._onFilterChange('state', value)}
          options={this._getJobStatusOptions()}
          value={this.state.filter.state}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        subText={this.state.filter.tags.length === 1 ? this.state.filter.tags[0] : undefined}
        text={this._getTagFilterText()}
      >
        <Components.ActionPanel.FilterTagDropdown
          onChange={value => this._onFilterChange('tags', value)}
          options={this.props.jobTags.map(option => ({ value: option, text: option }))}
          placeholder={this.props.strings.Filter.buttons.tag.placeholder}
          value={this.state.filter.tags}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        subText={(!Helper.isNullOrUndefined(this.state.filter.time_from)
          && !Helper.isNullOrUndefined(this.state.filter.time_to))
          ? this._getSubTextJobTimeRange() : undefined}
        text={this.props.strings.Filter.buttons.timeRange.jobTimeRange}
      >
        <Components.ActionPanel.FilterTimeCheckbox
          onChange={(fieldName, v) => this._onFilterChange(fieldName, v)}
          options={this.props.timeWindows}
          values={this.state.filter}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        text={this._getTypeFilterText()}
      >
        <Components.Input.CheckboxGroup.WithArray
          onChange={value => this._onFilterChange('job_type', value)}
          options={this._getJobTypeOptions()}
          value={this.state.filter.job_type}
        />
      </Components.ActionPanel.DropdownButton>
    </Components.ActionPanel.FilterPanel>
  );

  _renderMultiSelectPanel = () => (
    <Components.ActionPanel.MultiSelectPanel
      checkedStatus={this.state.checkedStatus}
      dataIds={this.props.jobs.map(job => job.id)}
      updateCheckedStatus={this._updateCheckedStatus}
    >
      <span className="vf-job__page__multi-select-panel__count">
        {TranslationHelper.replaceTag(this.props.strings.Job.page.multiSelectCount, {
          count: (
            <span className="vf-job__page__multi-select-panel__count--number">
              {Object.keys(this.state.checkedStatus)
                .filter(key => this.state.checkedStatus[key]).length}
            </span>
          ),
        })}
      </span>

      <Components.Button
        iconClassName="versa-completed"
        id="vf-submissions__jobs__bulk-submit-button"
        loading={this.props.isSubmittingJob}
        onClick={this._submitMultiple}
      >
        {this.props.strings.Job.page.buttons.submit}
      </Components.Button>

      <Components.Button
        iconClassName="versa-retract"
        iconText=""
        id="vf-submissions__jobs__bulk-revoke-button"
        loading={this.props.isRevokingJob}
        onClick={this._revokeMultiple}
      >
        {this.props.strings.Job.page.buttons.revoke}
      </Components.Button>

      <Components.Button
        disabled={this.state.disableButton.print}
        hint={this.props.strings.Job.page.buttons.print}
        iconClassName="versa-print"
        id="vf-submissions__tasks__bulk-print-button"
        onClick={this._printMultiple}
      />

      {!isUserReadOnly() ? (
        <Components.Archive
          archive={this._archiveMultiple}
          archivePath={() => Utils.Router.transitionTo('/archives/jobs')}
          ids={this._getSelectedIds()}
          modelName={this.props.strings.Job.constants.job}
          updateContent={() => { this._loadList(); }}
        >
          <Components.Button
            hint={this.props.strings.Job.page.buttons.archive}
            iconClassName="versa-archive"
            id="vf-submissions__jobs__bulk-archive-button"
          />
        </Components.Archive>
      ) : false}
    </Components.ActionPanel.MultiSelectPanel>
  )

  _renderActionPanel() {
    const { meta, strings } = this.props;
    const { filter } = this.state;

    const sortOption = SORT_OPTIONS.find(option => option.value === filter.sort_by);
    const sortButtonText = Formatter.capitalizeEachWord(sortOption[`${filter.order_by}endingText`]);

    return (
      <Components.ActionPanel
        renderFilterPanel={this._renderFilterPanel}
        renderMultiSelectPanel={this._renderMultiSelectPanel}
      >
        <Components.Input.ButtonSelect
          id="vf-submissions__jobs__model-toggle"
          onChange={this._onModelChange}
          options={this._getModelOptions()}
          value="job"
        />
        <Components.Paginator
          currentPage={meta.page}
          id="vf-submissions__jobs__paginator"
          isCountCustomizable
          itemsPerPage={meta.per_page}
          model="jobs"
          onChange={this._onFilterChange}
          totalItems={meta.total}
        />
        <Components.ActionPanel.DropdownButton
          showArrow
          text={Helper.getRangeDateString(
            this.state.filter.date[0], this.state.filter.date[1], this.props.account.language,
          )}
        >
          <Components.ActionPanel.FilterDateRangePicker
            locale={this.props.account.language}
            onChange={value => this._onFilterChange('date', value)}
            values={this.state.filter.date}
          />
        </Components.ActionPanel.DropdownButton>
        <Components.ActionPanel.FilterTagSearch
          id="vf-submissions__jobs__search-input"
          onChange={value => this._onFilterChange('keyword', value)}
        />
        <Components.ActionPanel.FilterShowButton
          onClick={() => { this._toggleShowFilter(); }}
          showFilter={this.state.showFilter}
        />
        <Components.ActionPanel.DropdownButton
          iconClassName="versa-sort"
          id="vf-submissions__jobs__filter-tuner"
          text={sortButtonText}
        >
          <Components.Sorter
            id="vf-submissions__jobs__filter-tuner__sort"
            onChange={value => this._onFilterChange(null, value)}
            options={SORT_OPTIONS}
            orderValue={this.state.filter.order_by}
            value={this.state.filter.sort_by}
          />
        </Components.ActionPanel.DropdownButton>
        <Components.ActionPanel.ButtonGroup>
          {!isUserReadOnly() ? (
            <Components.Button
              hint={strings.Job.page.buttons.add}
              iconClassName="versa-add"
              id="vf-submissions__jobs__new-job-button"
              onClick={this._viewNewJobForm}
            />
          ) : false}
          {!isUserReadOnly() ? (
            <Components.Button
              hint={strings.Job.page.buttons.import}
              iconClassName="versa-upload"
              id="vf-submissions__jobs__import-button"
              onClick={this._viewImportJobForm}
            />
          ) : false}
          <Components.Button
            hint={strings.Job.page.buttons.export}
            iconClassName="versa-download"
            id="vf-submissions__jobs__export-button"
            onClick={this._viewExportJobForm}
          />
        </Components.ActionPanel.ButtonGroup>
      </Components.ActionPanel>
    );
  }

  _renderPageContent() {
    return this.props.jobs.map(job => (
      <JobModule.ListItem
        account={this.props.account}
        checkItem={this._checkItem}
        checkedStatus={this.state.checkedStatus[job.id]}
        data={job}
        dispatch={this.props.dispatch}
        id="vf-submissions__jobs__list-item"
        isRevokingJob={this.props.isRevokingJob}
        isSubmittingJob={this.props.isSubmittingJob}
        key={job.id}
        showCancelModal={dataId => this._showCancelModal(dataId)}
      />
    ));
  }

  _renderCancelModal() {
    const strings = this.props.strings.Job.cancelDialog;

    return (
      <Components.Modal.Warning
        headerText={strings.header}
        leftButtonOnClick={() => this._confirmCancel()}
        leftButtonText={strings.confirmButton}
        rightButtonOnClick={() => this._hideCancelModal()}
        rightButtonText={strings.cancelButton}
        visible={this.state.showCancelModal}
      >
        {strings.line1}
        <br />
        <br />
        {strings.line2}
      </Components.Modal.Warning>
    );
  }

  render() {
    return (
      <div>
        {this._renderActionPanel()}
        {this._renderPageContent()}
        {this._renderCancelModal()}
        {this.props.children}
      </div>
    );
  }
}

SubmissionsJobPageContent.displayName = 'Submissions Job Page';

SubmissionsJobPageContent.propTypes = {
  account: PropTypes.shape(),
  children: PropTypes.node,
  dispatch: PropTypes.func.isRequired,
  filter: PropTypes.shape({
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    job_type: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isRevokingJob: PropTypes.bool,
  isSubmittingJob: PropTypes.bool,
  jobTags: PropTypes.arrayOf(PropTypes.string),
  jobs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
  })).isRequired,
  meta: PropTypes.shape({
    page: PropTypes.number,
    per_page: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  onModelChange: PropTypes.func.isRequired,
  strings: PropTypes.shape().isRequired,
  timeWindows: PropTypes.arrayOf(PropTypes.shape()),
};

SubmissionsJobPageContent.defaultProps = {
  account: null,
  children: null,
  isRevokingJob: false,
  isSubmittingJob: false,
  jobTags: [],
  timeWindows: [],
};

function mapStateToProps(state) {
  const {
    account, job, filter, constant,
  } = state;

  return {
    account: account.account,
    meta: job.jobMeta,
    jobs: job.jobsIds.map(id => job.jobs[id]),
    isRevokingJob: job.isRevokingJob,
    isSubmittingJob: job.isSubmittingJob,
    filter: filter.filter.myJobs,
    jobTags: constant.constant.jobs.tags,
    timeWindows: account.timeWindows,
  };
}

export default connect(mapStateToProps)(translate('')(SubmissionsJobPageContent));
